import { CatalogueReviews, CatalogueReviewProof } from '../../api/emailCatalogue';
import { isProviderEmail, proofMatches, reviewProof } from '../email-catalogue/reviewProofs';
import EmailSettingsReview from '../email-catalogue/EmailSettingsReview';
import React from "react";
import DOMPurify from "dompurify";
import {
  AuthoringCatalogue,
  CanonicalPreviewResult,
  IngestionProfileV1,
  ReadinessCode,
  WorkflowDefinition,
  WorkflowNode,
  WorkflowPreviewResult,
  PreviewMessage,
  LABELS,
  formatDuration,
  isProtectedKind,
  portLabelFor,
  portsFor,
} from "./workflowTypes";
import { isIntakeNode } from "./WorkflowIntakeFields";

export const READINESS_LABELS: Record<ReadinessCode, string> = {
  ready:
    "Ready: an exact existing published definition is active and reused",
  workflow_paused:
    "Expected: prepared definitions stay paused. Publishing never activates them; activation and pattern binding are separate, audited steps",
  missing_recognition:
    "Recognition missing: no approved pattern recognises this source yet",
  missing_identity:
    "Identity slot missing: the pattern names no claim reference or agreement number (informational)",
  verified_input_gap:
    "Verified input gap: extraction or absence facts still need source proof",
  missing_lender_source_evidence: "Lender source evidence is missing",
  missing_settings: "Protected business settings are incomplete",
  source_review_required:
    "Source review required: a publisher must review source-specific facts",
};
const NORMAL_READINESS: ReadinessCode[] = ["ready", "workflow_paused"];
export const finalResponseLabel = (profile: IngestionProfileV1) =>
  !Object.prototype.hasOwnProperty.call(profile, "final_response_fact")
    ? "not recorded (historical profile)"
    : !profile.final_response_fact
      ? "not set (no reusable fact)"
      : profile.final_response_fact.value
        ? `Yes, a final response (evidence ${profile.final_response_fact.evidence_ref || "missing"})`
        : `No, not a final response (evidence ${profile.final_response_fact.evidence_ref || "missing"})`;

/** Source, provenance and synthetic-only effect summary for one intake block. */
function IntakeSummary({
  node,
  definition,
  catalogue,
}: {
  node: WorkflowNode;
  definition: WorkflowDefinition;
  catalogue?: AuthoringCatalogue | null;
}) {
  const c = node.config;
  if (node.kind === "condition")
    return (
      <>
        <p>All three branches are shown; this does not predict which one a real claim takes.</p>
        <ul>
          {portsFor(node).map((port) => (
            <li key={port}>
              {portLabelFor(node, port, catalogue)} →{" "}
              {definition.edges.find(
                (edge) => edge.source === node.id && edge.port === port,
              )?.target || "Not connected"}
            </li>
          ))}
        </ul>
      </>
    );
  if (node.kind === "ingest_claim_correspondence" && c.profile) {
    const profile = c.profile;
    return (
      <>
        <p>
          Category {profile.category} from reviewed source{" "}
          {profile.category_origin.reviewed_entry_id}, version{" "}
          {profile.category_origin.reviewed_version} (source hash{" "}
          <span className="wf-hash">{profile.category_origin.source_hash}</span>).
        </p>
        <p>Final response fact: {finalResponseLabel(profile)}.</p>
        <p>
          Secondary commission fact:{" "}
          {profile.secondary_commission_fact?.value || "not set"}. Extraction:{" "}
          {profile.extraction.enabled
            ? `${profile.extraction.mappings.length} allowed field mapping(s)`
            : "off"}
          .
        </p>
        <p>Synthetic only. No claim, correspondence or task was written.</p>
      </>
    );
  }
  if (node.kind === "finish_intake_task") {
    const required = Object.values(c.requires || {}).filter(Boolean);
    return (
      <p>
        Outcome {String(c.outcome)}.{" "}
        {required.length
          ? `Requires ${required.join(", ")}`
          : "Requires no predecessor receipts"}
        . Synthetic only; the source task was not changed.
      </p>
    );
  }
  return (
    <p>
      Synthetic only. {LABELS[node.kind]} performed no claim, task or email
      effect in this preview.
    </p>
  );
}

export function SafeEmailPreview({
  html,
  title,
}: {
  html: string;
  title: string;
}) {
  const clean = DOMPurify.sanitize(html, {
    FORBID_TAGS: [
      "script",
      "style",
      "iframe",
      "object",
      "embed",
      "form",
      "input",
      "button",
      "link",
      "meta",
      "base",
      "video",
      "audio",
      "source",
      "svg",
      "math",
    ],
    FORBID_ATTR: [
      "href",
      "src",
      "srcset",
      "action",
      "formaction",
      "background",
      "poster",
      "style",
      "target",
      "ping",
    ],
  });
  const document = `<!doctype html><html><head><meta http-equiv="Content-Security-Policy" content="default-src 'none'; img-src 'none'; style-src 'unsafe-inline'; form-action 'none'; base-uri 'none'"><style>body{font:16px/1.5 system-ui;color:#172b42;overflow-wrap:anywhere;padding:12px}</style></head><body>${clean}</body></html>`;
  return (
    <iframe
      title={title}
      sandbox=""
      referrerPolicy="no-referrer"
      srcDoc={document}
      className="wf-email-preview"
    />
  );
}
function Value({ value }: { value: unknown }) {
  if (Array.isArray(value))
    return (
      <ul>
        {value.map((item, index) => (
          <li key={index}>
            <Value value={item} />
          </li>
        ))}
      </ul>
    );
  if (value && typeof value === "object")
    return (
      <dl>
        {Object.entries(value).map(([key, item]) => (
          <React.Fragment key={key}>
            <dt>{key}</dt>
            <dd>
              <Value value={item} />
            </dd>
          </React.Fragment>
        ))}
      </dl>
    );
  return <span>{String(value ?? "Missing")}</span>;
}
export function MessagePreview({ message }: { message: PreviewMessage }) {
  if (isProviderEmail(message)) return <section className="wf-preview-message">
    <p>{message.conditional_explanation}</p>
    <EmailSettingsReview review={message.settings} title={message.purpose} />
    <p>Occurrence: {message.occurrence} · option revision {message.catalogue_ref.revision}</p>
  </section>;
  return (
    <section className="wf-preview-message">
      <h4>{message.rendered.subject}</h4>
      <p>
        Recipient source: {message.recipient} · Template {message.template_id},
        source version {message.version}
      </p>
      <p>
        Reviewed local import. Remote content equivalence is unverified.{" "}
        {message.provider_metadata
          ? "Remote metadata was captured; publication rechecks it."
          : "Remote metadata drift is untracked."}
      </p>
      {message.placeholder_outputs.length > 0 && (
        <p>
          Inactive placeholder variables:{" "}
          {message.placeholder_outputs.join(", ")}. No secure link was created.
        </p>
      )}
      <SafeEmailPreview
        html={message.rendered.html}
        title={`Email preview ${message.template_id}`}
      />
      <details>
        <summary>Plain text and resolved variable sources</summary>
        <pre>{message.rendered.text}</pre>
        {message.token_provenance ? (
          Object.entries(message.token_provenance).map(([name, source]) => {
            const value = name
              .split(".")
              .reduce<unknown>(
                (current, key) =>
                  current && typeof current === "object"
                    ? (current as Record<string, unknown>)[key]
                    : undefined,
                message.token_values,
              );
            return (
              <section key={name} aria-label={`Resolved variable ${name}`}>
                <h5>{name}</h5>
                {source.origin === "protected_output" ? (
                  <p>
                    Protected detail · {source.mapping?.output} ·{" "}
                    {source.mapping?.type} / {source.mapping?.format}. Filled
                    from the verified claim when sent; synthetic here, and any
                    link is inactive.
                  </p>
                ) : source.origin === "business_placeholder" ? (
                  <p>
                    Business-owned placeholder · {source.owner} · variable{" "}
                    {source.variable} · {source.type} / {source.format}. Not a
                    generic catalogue field or actual business outcome.
                  </p>
                ) : (
                  <>
                    <p>
                      Source:{" "}
                      {source.origin === "node_output"
                        ? `${source.mapping?.node_id}.${source.mapping?.output}`
                        : source.mapping?.source}{" "}
                      · {source.mapping?.type} / {source.mapping?.format}
                    </p>
                    <Value value={source.source_metadata} />
                    {source.origin === "pattern_slot" && (
                      <p>
                        Required pattern input {source.mapping?.slot}:{" "}
                        {source.mapping?.slot_type} /{" "}
                        {source.mapping?.input_format} from{" "}
                        {source.mapping?.source_name},{" "}
                        {source.mapping?.partition} partition. Fixed synthetic
                        placeholder, not a matched source or authoritative
                        business fact. Exact pattern approval and admission must
                        verify compatibility.
                      </p>
                    )}
                  </>
                )}
                <p>Synthetic resolved value:</p>
                <Value value={value} />
              </section>
            );
          })
        ) : (
          <>
            <p>
              Source provenance unavailable in this response. Run a new preview
              to inspect the mapping.
            </p>
            <Value value={message.token_values} />
          </>
        )}
      </details>
    </section>
  );
}
export default function WorkflowPreview({
  result,
  reviewed,
  onReview,
  catalogue,
  reviewedCatalogue = {}, onCatalogueReview, readOnly = false,
}: {
  result: WorkflowPreviewResult;
  reviewed: Record<string, string>;
  onReview: (id: string, hash: string, checked: boolean) => void;
  catalogue?: AuthoringCatalogue | null;
  reviewedCatalogue?: CatalogueReviews;
  onCatalogueReview?: (occurrence: string, proof: CatalogueReviewProof, checked: boolean) => void;
  readOnly?: boolean;
}) {
  return (
    <section aria-label="Workflow preview" className="wf-preview">
      <h3>Synthetic workflow preview</h3>
      <p>{result.notice}</p>
      <p>
        All branches are shown for review; this is not a prediction of which
        branch a real claim will take. Provider acceptance is not delivery.
      </p>
      {result.nodes.map((node) => {
        const graphNode = result.definition.nodes.find(
          (item) => item.id === node.node_id,
        );
        return (
        <section key={node.node_id} aria-label={`Preview ${node.node_id}`}>
          <h4>
            {LABELS[node.kind]} · {node.node_id}
          </h4>
          {graphNode && isIntakeNode(graphNode) && (
            <IntakeSummary
              node={graphNode}
              definition={result.definition}
              catalogue={catalogue}
            />
          )}
          {node.status === "held" && (
            <p className="wf-warning">
              Preview held: {node.message} ({node.reason_code})
            </p>
          )}
          {node.target_policy && <p>{node.target_policy}</p>}
          {node.synthetic_claim_ids && (
            <p>
              Synthetic selected claims: {node.synthetic_claim_ids.join(", ")}.
              Example census:{" "}
              {node.synthetic_claim_examples
                ?.map((claim) => `${claim.id} (${claim.status})`)
                .join("; ")}
              .
            </p>
          )}
          {node.fee_policy && <p>{node.fee_policy}</p>}
          {node.response_policy && (
            <p>
              Reminder email is sent{" "}
              {formatDuration(node.response_policy.timeout_seconds)} after the
              email provider accepted the first email, unless the client replies
              or staff record a response first.
            </p>
          )}
          {!!node.attachments?.length && (
            <p>
              Intended attachments: {node.attachments.join(", ")}. No real files
              were loaded.
            </p>
          )}
          {node.messages.map((message, index) => (
            <div key={isProviderEmail(message) ? message.occurrence : `${message.template_id}-${index}`}>
              {isProtectedKind(node.kind) && (
                <h5>{index === 0 ? "Client email" : "Reminder email"}</h5>
              )}
              <MessagePreview message={message} />
              {!readOnly && (isProviderEmail(message) ? <label className="wf-check"><input type="checkbox"
                disabled={!reviewProof(message)} checked={proofMatches(message, reviewedCatalogue[message.occurrence])}
                onChange={event => { const proof = reviewProof(message); if (proof) onCatalogueReview?.(message.occurrence, proof, event.target.checked); }} />
                I reviewed {message.purpose} for this occurrence
              </label> : <label className="wf-check">
                <input
                  type="checkbox"
                  checked={
                    reviewed[message.template_id] === message.source_hash
                  }
                  onChange={(e) =>
                    onReview(
                      message.template_id,
                      message.source_hash,
                      e.target.checked,
                    )
                  }
                />
                I reviewed template {message.template_id}, version{" "}
                {message.version}, for publication
              </label>)}
            </div>
          ))}
        </section>
        );
      })}
    </section>
  );
}

/**
 * No-write canonical preparation result. Readiness is informational: a paused
 * definition is the expected state, and incomplete settings arrive as
 * exceptions because no valid definition can be built without them.
 */
export function CanonicalProposalPreview({
  result,
  catalogue,
  onOpenDraft,
}: {
  result: CanonicalPreviewResult;
  catalogue?: AuthoringCatalogue | null;
  onOpenDraft: (workflowId: string, definition: WorkflowDefinition) => void;
}) {
  const label = (code: string) =>
    READINESS_LABELS[code as ReadinessCode] || code.replace(/_/g, " ");
  return (
    <section aria-label="Canonical proposals" className="wf-preview">
      <h3>Canonical preparation preview</h3>
      <p>
        Nothing was saved, published, activated or bound. Readiness grants
        nothing; each selected source still needs its own reviewed evidence.
      </p>
      <h4>Definitions ({result.definitions.length})</h4>
      {result.definitions.map((item) => (
        <article
          key={item.definition_key}
          aria-label={`Definition ${item.definition_key}`}
          className="wf-preview-message"
        >
          <h5>{item.definition.name}</h5>
          <dl>
            <dt>Behavior hash</dt>
            <dd className="wf-hash">{item.behavior_hash}</dd>
            <dt>Exemplar graph hash</dt>
            <dd className="wf-hash">{item.graph_hash}</dd>
            {item.profile_hashes.map((row) => (
              <React.Fragment key={row.node_id}>
                <dt>Profile hash · {row.node_id}</dt>
                <dd className="wf-hash">{row.profile_hash}</dd>
              </React.Fragment>
            ))}
          </dl>
          {item.reuse_pin ? (
            <p>
              Reuses published workflow {item.reuse_pin.workflow_id} version{" "}
              {item.reuse_pin.workflow_version} exactly. Load it from Existing
              workflow to inspect it.
            </p>
          ) : (
            <>
              <p>
                Prospective workflow ID caf-canonical-{item.behavior_hash},
                version 1. This does not assert that it has been published.
              </p>
              <button
                type="button"
                onClick={() =>
                  onOpenDraft(
                    `caf-canonical-${item.behavior_hash}`,
                    item.definition,
                  )
                }
              >
                Open as a new unsaved draft (replaces the local draft)
              </button>
            </>
          )}
        </article>
      ))}
      <h4>Source mappings ({result.mappings.length})</h4>
      {result.mappings.map((item) => (
        <section
          key={`${item.entry_id}-${item.reviewed_version}`}
          aria-label={`Mapping ${item.entry_id}`}
          className="wf-preview-message"
        >
          <h5>
            {item.entry_id} · {item.source_sender} · reviewed version{" "}
            {item.reviewed_version}
          </h5>
          <p
            className={
              NORMAL_READINESS.includes(item.executable_readiness)
                ? "wf-fixed"
                : "wf-warning"
            }
          >
            {label(item.executable_readiness)}.
          </p>
          <p>
            Definition {item.definition_key}. Selected profile hashes:{" "}
            {item.selected_profile_hashes
              .map((row) => `${row.node_id} ${row.profile_hash}`)
              .join("; ") || "none"}
            .
          </p>
          {item.eligibility.gaps.length ? (
            <ul aria-label={`Evidence gaps for ${item.entry_id}`}>
              {item.eligibility.gaps.map((gap, index) => (
                <li key={index}>
                  {label(gap.code)}: {gap.node_id ? `${gap.node_id}.` : ""}
                  {gap.path} ({gap.kind})
                </li>
              ))}
            </ul>
          ) : (
            <p>No evidence gaps reported for this source.</p>
          )}
          <p>
            Source text is not included in this preview. Open the reviewed
            entry to read it.
          </p>
        </section>
      ))}
      <section aria-label="Preparation exceptions">
        <h4>Exceptions ({result.exceptions.length})</h4>
        {result.exceptions.length ? (
          <ul>
            {result.exceptions.map((item, index) => (
              <li key={`${item.entry_id}-${index}`}>
                {item.entry_id} ({item.source_sender}): {label(item.code)}
                {item.field ? ` · field ${item.field}` : ""}
              </li>
            ))}
          </ul>
        ) : (
          <p>No exceptions.</p>
        )}
        {catalogue?.parity_inspector && (
          <p>
            Named readiness codes:{" "}
            {catalogue.parity_inspector.readiness_codes.join(", ")}.
          </p>
        )}
      </section>
    </section>
  );
}
